import React, { useRef, useState, ReactNode, useEffect } from 'react';
import { motion } from 'framer-motion';

interface MagneticProps {
  children: ReactNode;
  strength?: number;
}

// Shared mobile detection state — updated by a single resize observer
// Use a module-level Set to track subscribers without global listener at module init
let isMobileCache = false;

// Lazy-initialize the global resize listener only when the first Magnetic mounts
let globalListenerInitialized = false;
const mobileListeners = new Set<() => void>();

function computeIsMobile() {
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    window.innerWidth < 960
  );
}

function ensureGlobalListener() {
  if (globalListenerInitialized) return;
  globalListenerInitialized = true;
  isMobileCache = computeIsMobile();
  window.addEventListener('resize', () => {
    isMobileCache = computeIsMobile();
    mobileListeners.forEach(fn => fn());
  }, { passive: true });
}

export function Magnetic({ children, strength = 0.5 }: MagneticProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDisabled, setIsDisabled] = useState(() => {
    // Safe: called during render, window is available client-side
    if (typeof window === 'undefined') return true;
    return computeIsMobile();
  });

  useEffect(() => {
    ensureGlobalListener();
    isMobileCache = computeIsMobile();
    setIsDisabled(isMobileCache);

    const update = () => setIsDisabled(isMobileCache);
    mobileListeners.add(update);
    return () => { mobileListeners.delete(update); };
  }, []);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!ref.current || isDisabled) return;
    const { clientX, clientY } = e;
    const { left, top, width, height } = ref.current.getBoundingClientRect();
    const centerX = left + width / 2;
    const centerY = top + height / 2;
    const x = (clientX - centerX) * strength;
    const y = (clientY - centerY) * strength;
    setPosition({ x, y });
  };

  const handleMouseLeave = () => {
    if (isDisabled) return;
    setPosition({ x: 0, y: 0 });
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      animate={isDisabled ? {} : { x: position.x, y: position.y }}
      transition={{ type: "spring", stiffness: 150, damping: 15, mass: 0.1 }}
      style={{ display: 'inline-block' }}
    >
      {children}
    </motion.div>
  );
}
